import React, { useState } from "react";
import { Camera, Loader2, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import useConfig from "@/hooks/useConfig";
import { Chip } from "@/v2/components/ui/chip";
import { useUpdatePhoto, useUpdateProfile, UserProfileData } from "../api";

interface ProfileInformationSectionProps {
  profile: UserProfileData;
  firstName: string;
  lastName: string;
  setFirstName: (v: string) => void;
  setLastName: (v: string) => void;
}

export function ProfileInformationSection({
  profile,
  firstName,
  lastName,
  setFirstName,
  setLastName,
}: ProfileInformationSectionProps) {
  const configData = useConfig();

  const updateProfileMutation = useUpdateProfile();
  const updatePhotoMutation = useUpdatePhoto();

  const [imageError, setImageError] = useState(false);

  const getInitials = () => {
    if (!profile) return "US";
    const first = profile.FirstName ? profile.FirstName.charAt(0) : "";
    const last = profile.LastName ? profile.LastName.charAt(0) : "";
    return `${first}${last}`.toUpperCase() || "US";
  };

  const getFullImageUrl = (url?: string) => {
    if (!url) return "";
    if (
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("data:") ||
      url.startsWith("//")
    ) {
      return url;
    }
    return `${configData?.s3HostUrl || ""}${url}`;
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size exceeds 5MB limit");
      input.value = "";
      return;
    }

    updatePhotoMutation.mutate(file, {
      onSuccess: () => {
        // The mutation already syncs localStorage + notifyAuthChanged, so the
        // page (which reads useAuthState) refreshes without a Redux dispatch.
        toast.success("Profile image updated successfully");
        setImageError(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || "Failed to upload image");
      },
      // Clear the input so re-selecting the same file (e.g. after an error) fires onChange again.
      onSettled: () => {
        input.value = "";
      },
    });
  };

  const handleSaveName = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("First name and Last name cannot be empty");
      return;
    }

    updateProfileMutation.mutate(
      { first_name: firstName.trim(), last_name: lastName.trim() },
      {
        onSuccess: () => {
          toast.success("Name updated successfully");
        },
        onError: (error: any) => {
          toast.error(
            error.response?.data?.message || "Failed to update profile",
          );
        },
      },
    );
  };

  // Compare trimmed values, since Save submits trimmed names — otherwise
  // trailing/leading whitespace would leave the button stuck enabled.
  const originalFirstName = (profile?.FirstName || "").trim();
  const originalLastName = (profile?.LastName || "").trim();
  const isNameChanged =
    firstName.trim() !== originalFirstName ||
    lastName.trim() !== originalLastName;

  return (
    // Vertical padding is inline: Tailwind p/py utilities resolve to 0 inside
    // .v2 (the app's unlayered `* { padding: 0 }` outranks them), so the extra
    // room around the section divider has to be set here.
    <div
      className="px-4 sm:px-6"
      style={{ paddingTop: "34px", paddingBottom: "34px" }}
    >
      <div className="flex flex-col xl:flex-row items-start gap-8">
        {/* Avatar wrapper */}
        <div className="flex flex-col items-center flex-shrink-0">
          {/* One shared file input; both the avatar and the button open it. */}
          <input
            type="file"
            id="profile-photo-input"
            accept="image/*"
            className="hidden"
            onChange={handlePhotoChange}
            disabled={updatePhotoMutation.isPending}
          />

          {/* The avatar itself is a label for the file input, so clicking it
              opens the picker (in addition to the Change Photo button). */}
          <div className="relative">
            <label
              htmlFor="profile-photo-input"
              aria-label="Change photo"
              title="Change photo"
              className={`group w-32 h-32 rounded-full bg-action text-white flex items-center justify-center text-4xl font-semibold relative overflow-hidden shadow-inner ${updatePhotoMutation.isPending ? "cursor-wait" : "cursor-pointer"}`}
            >
              {updatePhotoMutation.isPending ? (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-white" />
                </div>
              ) : profile?.ProfileImage &&
                profile.ProfileImage !== "Profile" &&
                !imageError ? (
                <img
                  src={getFullImageUrl(profile.ProfileImage)}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                getInitials()
              )}
              {/* Always-visible centered camera so it's obvious the avatar is
                  editable. A contained dark disc keeps it legible over a photo
                  or the plain colored circle; it darkens slightly on hover. */}
              {!updatePhotoMutation.isPending && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-12 h-12 rounded-full bg-black/40 group-hover:bg-black/55 transition-colors flex items-center justify-center">
                    <Camera className="w-6 h-6 text-white" strokeWidth={2} />
                  </span>
                </span>
              )}
            </label>
          </div>

          <label
            htmlFor="profile-photo-input"
            className="mt-4 cursor-pointer px-3 h-9 border-2 rounded-md font-semibold text-sm whitespace-nowrap hover:bg-accent hover:text-accent-foreground transition-colors w-full sm:w-auto"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              color: "var(--v2-action)",
              borderColor: "var(--v2-action)",
            }}
          >
            <Camera
              className="w-4 h-4 flex-shrink-0"
              style={{ color: "var(--v2-action)" }}
            />
            <span className="leading-none select-none">Change Photo</span>
          </label>
          <span className="text-xs mt-2 font-normal text-muted-foreground">
            JPG or PNG. Max 5MB.
          </span>
        </div>

        {/* Input Fields */}
        <div className="flex-1 w-full space-y-4">
          {/* First Name & Last Name */}
          <div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label
                  className="block text-sm font-medium mb-2 text-muted-foreground"
                  style={{ color: "#334155" }}
                >
                  First Name
                </label>
                <input
                  type="text"
                  className="w-full min-w-0 px-2 h-10 border border-border bg-card rounded-md text-sm focus:outline-none focus:border-action font-normal text-foreground"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>

              {/* Last Name */}
              <div>
                <label
                  className="block text-sm font-medium mb-2 text-muted-foreground"
                  style={{ color: "#334155" }}
                >
                  Last Name
                </label>
                <input
                  type="text"
                  className="w-full min-w-0 px-2 h-10 border border-border bg-card rounded-md text-sm focus:outline-none focus:border-action font-normal text-foreground"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <span className="text-sm block mt-2 font-normal text-muted-foreground">
              This is how your name will appear on{" "}
              {configData?.title || "NA TV"}.
            </span>
          </div>

          {/* Email Address */}
          <div>
            <label
              className="block text-sm font-medium mb-2 text-muted-foreground"
              style={{ color: "#334155" }}
            >
              Email Address
            </label>
            <div className="flex flex-col xl:flex-row xl:items-center gap-2">
              <div className="relative flex-1 flex items-center min-w-0">
                <input
                  type="email"
                  disabled
                  className="w-full min-w-0 px-2 h-10 border border-border bg-muted rounded-md text-sm cursor-not-allowed font-normal text-muted-foreground xl:pr-24"
                  value={profile?.Email || ""}
                />
                {profile?.IsEmailVerified && (
                  <div className="hidden xl:flex absolute right-3 top-1/2 -translate-y-1/2">
                    <Chip color="var(--v2-success)" icon={Check} size="sm">
                      Verified
                    </Chip>
                  </div>
                )}
              </div>
              {profile?.IsEmailVerified && (
                <div className="flex xl:hidden self-start">
                  <Chip color="var(--v2-success)" icon={Check} size="sm">
                    Verified
                  </Chip>
                </div>
              )}
            </div>
            <span className="text-sm block mt-2 font-normal text-muted-foreground">
              This is your email for account notifications and sign in.
            </span>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSaveName}
              disabled={updateProfileMutation.isPending || !isNameChanged}
              className="mt-3 h-11 px-3 text-white hover:bg-action-hover font-bold rounded-lg transition-colors flex items-center justify-center gap-2 flex-shrink-0 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "var(--v2-action)" }}
            >
              {updateProfileMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Save <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
